
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import ProductTable from '../../components/docs/ProductTable';
export const metadata = {
  toc: true,
  maxTocLevel: 5,
  layout: 'guide',
  description: 'Overview and comparison of Plaid financial insights solutions'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Financial insights products' subtitle='Review and compare solutions' subsubtitle="Overview and comparison of financial insights solutions" bottomBorder={true} mdxType="Header" />
    <p>{`These products serve use cases such as personal finance and budgeting, loan payback and debt management, expense and accounting applications, and wealth management and investing. Note that none of these products may be used as part of a credit or underwriting decisioning process; for underwriting use cases, see `}<a parentName="p" {...{
        "href": "/docs/underwriting"
      }}>{`credit underwriting products`}</a>{`.`}</p>
    <h4 {...{
      "id": "transactions"
    }}>{`Transactions`}</h4>
    <p><a parentName="p" {...{
        "href": "/docs/transactions"
      }}>{`Transactions`}</a>{` gets transaction data for an end user's account, such as a checking, savings, credit card, mortgage, or student loan account. Transaction data includes details such as date, merchant, and category; for some transactions, it will also include location data and a merchant name that has been enhanced for greater human readability. Any transaction data fields not provided to Plaid directly by the financial institution are derived via Plaid's ML-powered transactions enrichment engine. New and updated transactions are typically extracted between one and four times per day, depending on the institution.`}</p>
    <h5 {...{
      "id": "transactions-refresh"
    }}>{`Transactions Refresh`}</h5>
    <p><a parentName="p" {...{
        "href": "/docs/transactions"
      }}>{`Transactions Refresh`}</a>{` is an optional add-on for Transactions that can be used to trigger an on-demand, real-time update of transactions data. Transactions Refresh provides on-demand updates only; Plaid does not offer a real-time feed of transactions updates.`}</p>
    <h5 {...{
      "id": "recurring-transactions"
    }}>{`Recurring Transactions`}</h5>
    <p><a parentName="p" {...{
        "href": "/docs/transactions/#recurring-transactions"
      }}>{`Recurring Transactions`}</a>{`, available in the US, CA, and UK, is an optional add-on for Transactions that can be used to identify recurring transaction streams. Recurring Transactions is typically used by financial management apps to power features such as budget analysis and helping users identify and cancel unwanted subscriptions. `}</p>
    <h4 {...{
      "id": "enrich"
    }}>{`Enrich`}</h4>
    <p><a parentName="p" {...{
        "href": "/docs/enrich"
      }}>{`Enrich`}</a>{` allows you to provide your own transactions data and uses the same ML-powered enrichment engine used by the Transactions product to enhance this data with more details, such as category, location, and merchant name. Unlike most other Plaid products, Enrich does not use Link to connect to an end user's financial account; Enrich is designed for customers who already have transactions data from a non-Plaid source and would like to enhance it to provide better insights to their customers.`}</p>
    <h4 {...{
      "id": "liabilities"
    }}>{`Liabilities`}</h4>
    <p><a parentName="p" {...{
        "href": "/docs/liabilities"
      }}>{`Liabilities`}</a>{` gets details about an end user's debt, including interest rate, outstanding balance, repayment schedule, and details of the most recent repayment. Supported account types include credit cards, PayPal credit accounts, closed-end loans (student, mortgage, auto, personal, home equity, and commercial loans), and lines of credit, including home equity and commercial lines of credit. Liabilities does not provide transaction history. For full transaction and loan details, use both Liabilities and Transactions together.`}</p>
    <h4 {...{
      "id": "investments"
    }}>{`Investments`}</h4>
    <p><a parentName="p" {...{
        "href": "/docs/investments"
      }}>{`Investments`}</a>{` gets details about an end user's investment account, such as a brokerage account or 401(k). Investments comes with access to two main features: Investments Holdings, which provides details on the specific assets held, including type, description, value, cost basis, and acquisition date; and Investments Transactions, which provides details on transactions (such as trades, transfers, or dividends) within an investment account.`}</p>
    <h5 {...{
      "id": "investments-refresh"
    }}>{`Investments Refresh`}</h5>
    <p><a parentName="p" {...{
        "href": "/docs/investments"
      }}>{`Investments Refresh`}</a>{` is an optional add-on for Investments that can be used to trigger an on-demand, real-time update of investments data.`}</p>
    <h4 {...{
      "id": "transactions-investments-liabilities-and-enrich-comparison-table"
    }}>{`Transactions, Investments, Liabilities, and Enrich comparison table`}</h4>
    <ProductTable mdxType="ProductTable">
      {`
|                                                           | Transactions                       | Investments                                         | Liabilities                                        | Enrich                                              |
|-----------------------------------------------------------|------------------------------------|-----------------------------------------------------|----------------------------------------------------|-----------------------------------------------------|
| Summary                                                   | Details on transactions            | Details on investments and investments transactions | Details on loans, payments, balances, and interest | Enrich your existing transactions data |
| Main supported account types                              | Checking, savings, credit cards, loans | Brokerage accounts, including retirement            | Credit cards, PayPal credit accounts, closed-end loans, lines of credit | Checking, savings, credit cards                     |
| Supports brokerage accounts                               | No                                 | Yes                                                 | No                                                 | No                                                  |
| Provides transaction details                              | Yes                                | Yes                                                 | No (only most recent payment)                      | N/A                                                 |
| Provides ML-enhanced transaction descriptions and details | Yes                                | No                                                  | No                                                 | Yes                                                 |
| Provides interest rates                                   | No                                 | No                                                  | Yes                                                | No                                                  |
| Typical update frequency                                  | 1-4 times per day                  | At least once per day during market days (up to 2-4 times per day, depending on institution) | Once per day                                       | N/A                                                 |
| Optional Refresh add-on for on-demand updates             | Yes                                | Yes                                                 | No                                                 | N/A                                                 |
| Supported countries                                       | US, CA, UK, Europe                 | US, CA                                                  | US, CA (CA coverage limited)                       | US, CA                                                  |
| Billing plans available                                   | Pay-as-you-go or 12-month contract | Pay-as-you-go or 12-month contract                  | Pay-as-you-go or 12-month contract                 | Pay-as-you-go or 12-month contract                  |
`}
    </ProductTable>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "transactions",
        level: 4,
        title: "Transactions",
      },
{
        id: "enrich",
        level: 4,
        title: "Enrich",
      },
{
        id: "liabilities",
        level: 4,
        title: "Liabilities",
      },
{
        id: "investments",
        level: 4,
        title: "Investments",
      },
{
        id: "transactions-investments-liabilities-and-enrich-comparison-table",
        level: 4,
        title: "Transactions, Investments, Liabilities, and Enrich comparison table",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
